import React, { useState, useEffect, useRef } from 'react'
import './ContactDetailPage.css'
import httpClient from '../components/helpers/httpClient'
import { useNavigate, useParams } from "react-router-dom"
import MenuBar from "../components/MenuBar"
import Header from "../components/Header"
import LoadingSpinner from "../components/placeholders/LoadingSpinner"
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import Dropdown from '../components/Dropdown'
import CheckBox from '../components/CheckBox'
import DropdownMultiselectTags from '../components/DropdownMultiselectTags'
import { DateField } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import { toast } from "react-toastify"
import AddCompanyToContact from '../components/AddCompanyToContact'
import Note from '../components/Note'
import { PopupWindow, closePopupWindow } from '../components/PopupWindow'
import { formatDate, parseDateValue, serializeDate } from '../components/helpers/formatDate'
import { PopOutWindow } from '../components/PopOutWindow'
import { noteTypes } from '../assets/data/data'
import DropdownSpecialObject from '../components/DropdownSpecialObject'
import { showNotificationPopup } from '../components/NotificationPopup'
import { getNoteCreatorName } from '../components/helpers/getNoteCreatorName'
import { formatPropertyIdentifier } from '../components/helpers/propertyIdentifier'
import Button from '../components/UI/Button'

function ContactDetailPage() {
  const [contact, setContact] = useState([])
  const [contactTitle, setContactTitle] = useState("")
  const [isMarried, setIsMarried] = useState(false)
  const [partner, setPartner] = useState({ "id": null, "first_name": "", "last_name": "" })
  const [contactsList, setContactsList] = useState([{ "id": null, "first_name": null, "last_name": null }])
  const [birthday, setBirthday] = useState("")
  const [language, setLanguage] = useState("Dutch")
  const [edit, setEdit] = useState(false)
  const [tagsList, setTagsList] = useState([])
  const [tags, setTags] = useState([])
  const [companiesList, setCompaniesList] = useState([])
  const [companies, setCompanies] = useState([])
  const [linkNewCompany, setLinkNewCompany] = useState(false)
  const [loadingCompanyLink, setLoadingCompanyLink] = useState(false)
  const [expandedSection, setExpandedSection] = useState(null)
  const [notePopupIsActive, setNotePopupIsActive] = useState(false)
  const [noteDetailPopupIsActive, setNoteDetailPopupIsActive] = useState(false)
  const [notes, setNotes] = useState([])
  const [selectedNote, setSelectedNote] = useState(null)
  const [noteForm, setNoteForm] = useState({ noteDate: dayjs(), noteType: 'Note', subject: '', content: '' })
  const [editingNote, setEditingNote] = useState(null)
  const [projects, setProjects] = useState([])
  const [buildings, setBuildings] = useState([])
  const [properties, setProperties] = useState([])
  const [units, setUnits] = useState([])
  const [noteProject, setNoteProject] = useState({})
  const [noteBuilding, setNoteBuilding] = useState({})
  const [noteProperty, setNoteProperty] = useState({})
  const [noteUnit, setNoteUnit] = useState({})
  const [contactProperties, setContactProperties] = useState([])
  const [propertiesTab, setPropertiesTab] = useState("properties") // properties | rentals
  const [rentalContracts, setRentalContracts] = useState([])
  const [loadingRentalContracts, setLoadingRentalContracts] = useState(false)

  const navigate = useNavigate()
  const { contactID } = useParams()

  const formSectionAddressInfo = useRef(null)
  const formSectionOtherInfo = useRef(null)

  const fetchContact = async () => {
    const response = await httpClient.post(import.meta.env.VITE_API_URL + `/api/contacts/get?contactID=${contactID}`)

    response.data.language != null && setLanguage(response.data.language)
    setBirthday(getBirthdayStateValue(response.data.birthday))
    response.data.title != null && setContactTitle(response.data.title)
    response.data.partner != null && setPartner(response.data.partner)
    response.data.is_married != null && setIsMarried(response.data.is_married)

    setContact(response.data)
  }

  const fetchFullContacts = async () => {
    const response = await httpClient.get(import.meta.env.VITE_API_URL + "/api/contacts/get")
    setContactsList(response.data)
  }

  const fetchNotes = async () => {
    const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/notes/get/${contactID}?type=contact`)
    setNotes(response.data)
  }

  const fetchTagsList = async () => {
    const response = await httpClient.get(import.meta.env.VITE_API_URL + "/api/tags/get")
    setTagsList(response.data)
  }

  const fetchTags = async () => {
    const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/tags/get/${contactID}?type=contact`)
    setTags(response.data)
  }

  const fetchLinkedCompaniesList = async () => {
    const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/companies/get`)

    const data = response.data.map(company => {
      return ({
        "id": company.id,
        "company_name": company.company_name,
        "city": company.city
      })
    })

    setCompaniesList(data)
  }

  const fetchLinkedCompanies = async () => {
    const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/company-contacts/get/${contactID}?type=companies`)
    setCompanies(response.data)
  }

  const fetchProjects = async () => {
    const response = await httpClient.get(import.meta.env.VITE_API_URL + "/api/projects/get")
    setProjects(response.data)
  }

  const fetchContactProperties = async () => {
    try {
      const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/properties/properties-of-contact?contactID=${contactID}`)
      setContactProperties(response.data)
    } catch (error) {
      console.error("Error fetching contact properties:", error)
      setContactProperties([])
    }
  }

  const fetchContactRentalContracts = async () => {
    if (!contactID) return
    setLoadingRentalContracts(true)
    try {
      const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/rental-contracts/of-contact?contactID=${contactID}&detailed=true`)
      setRentalContracts(Array.isArray(response.data) ? response.data : [])
    } catch (error) {
      console.error("Error fetching contact rental contracts:", error)
      setRentalContracts([])
    } finally {
      setLoadingRentalContracts(false)
    }
  }

  const fetchBuildings = async () => {
    const data = {
      filters: {
        project_id: noteProject?.id,
      }
    }
    const response = await httpClient.post(import.meta.env.VITE_API_URL + "/api/buildings/get", data)
    setBuildings(response.data)
  }

  const fetchUnits = async () => {
    const data = {
      filters: {
        building_id: noteBuilding?.id
      }
    }
    const response = await httpClient.post(import.meta.env.VITE_API_URL + "/api/units/get", data)
    setUnits(response.data)
  }

  useEffect(() => {
    fetchBuildings()
  }, [noteProject])

  useEffect(() => {
    fetchUnits()
  }, [noteBuilding])

  useEffect(() => {
    fetchUnits()
  }, [noteProperty])

  useEffect(() => {
    fetchContact()
    fetchFullContacts()
    fetchTagsList()
    fetchTags()
    fetchLinkedCompaniesList()
    fetchLinkedCompanies()
    fetchNotes()
    fetchProjects()
    fetchContactProperties()
    fetchContactRentalContracts()

    const contactDetails = document.querySelector("#contact-details")
    const inputFields = contactDetails.querySelectorAll(".input_field")
    const checkboxes = contactDetails.querySelectorAll(".checkbox__container")
    const dropdowns = contactDetails.querySelectorAll(".dropdown__container-dropdown")

    const formElements = [inputFields, checkboxes, dropdowns]

    formElements.forEach(elementList => {
      if (elementList.length > 0) {
        elementList.forEach(element => {
          element.classList.add("disabled")
        })
      }
    })
  }, [])

  const handleGoBack = () => {
    navigate(-1)
  }

  const handleToggleSection = (sectionId) => {
    if (expandedSection === sectionId) {
      // If the section is already expanded, collapse it
      setExpandedSection(null)
    } else {
      // Expand the selected section
      setExpandedSection(sectionId)
    }
  }

  const handleRentalContractClick = (contractId) => {
    if (!contractId) return
    navigate(`/rental-contracts/${contractId}`)
  }

  const getBirthdayStateValue = (value) => {
    return parseDateValue(value)
  }

  const normalizeBirthdayForSave = (value) => {
    return serializeDate(value)
  }

  const validateEmailField = (inputId, errorId) => {
    const emailInput = document.getElementById(inputId)
    const errorElement = document.getElementById(errorId)

    if (!emailInput || !errorElement) return true

    const emailValidation = /\S+@\S+\.\S+/
    const value = emailInput.value?.trim()

    if (!value || emailValidation.test(value)) {
      emailInput.classList.remove('red-border')
      errorElement.classList.add('hide')
      return true
    }

    emailInput.classList.add('red-border')
    errorElement.classList.remove('hide')
    return false
  }

  const buildMailtoUrl = (email) => `mailto:${email}`

  const renderEmailField = ({ label, inputId, name, value, errorId }) => (
    <>
      <div className="input_field contact-email-field">
        <label className="input_field__label" htmlFor={inputId}>{label}</label>
        {
          edit ? (
            <input className="input_field__input_box--text" id={inputId} type="text" name={name} defaultValue={value} />
          ) : (
            <div className="contact-email-display">
              <span className={`contact-email-text ${value ? '' : 'is-empty'}`}>{value || ''}</span>
              {
                value && (
                  <a
                    className="contact-email-action"
                    href={buildMailtoUrl(value)}
                    title={`Send email to ${value}`}
                    aria-label={`Send email to ${value}`}
                  >
                    <MailOutlineIcon fontSize="small" />
                  </a>
                )
              }
            </div>
          )
        }
      </div>
      <div className="input_error hide" id={errorId}>I suspect this is not a valid email address 🤖</div>
    </>
  )

  const handleEditContact = async () => {
    if (edit) {
      const editButton = document.querySelector("#contacts__btn-edit")
      editButton.disabled = true

      let okToSave = true
      const formData = {}

      const inputElements = document.querySelectorAll('.input_field__input_box--text')

      inputElements.forEach(element => {
        const fieldName = element.name
        formData[fieldName] = element.value
      })

      formData["language"] = language
      formData["is_married"] = isMarried
      formData["partner_id"] = partner.id
      formData["birthday"] = normalizeBirthdayForSave(birthday)
      formData["title"] = contactTitle

      // let arrayRequiredTextBoxes = []

      // arrayRequiredTextBoxes.forEach((inputElement) => {
      //   if (!inputElement.value) {
      //     inputElement.classList.add("red-border")
      //   }
      // })

      // if (!arrayRequiredTextBoxes.every(item => item.value)) {
      //   okToSave = true
      // }

      okToSave = [
        validateEmailField('customers__business_email', 'input_error_business_email'),
        validateEmailField('customers__home_email', 'input_error_home_email')
      ].every(Boolean)

      if (!okToSave) {
        toast.error("Please complete all fields")
        editButton.disabled = false
        return
      }

      const response = await toast.promise(
        httpClient.put(import.meta.env.VITE_API_URL + `/api/contact/update?contactID=${contactID}`, formData),
        {
          pending: "Update contact...",
          success: "Contact has been update 👍",
          error: "Something went wrong 😢"
        }
      )
      console.log(response.data)

      fetchContact()

      editButton.disabled = false
      setEdit(!edit)
    } else {
      setEdit(!edit)

      const contactDetails = document.querySelector("#contact-details")
      const inputFields = contactDetails.querySelectorAll(".input_field")
      const checkboxes = contactDetails.querySelectorAll(".checkbox__container")
      const dropdowns = contactDetails.querySelectorAll(".dropdown__container-dropdown")

      const formElements = [inputFields, checkboxes, dropdowns]

      formElements.forEach(elementList => {
        if (elementList.length > 0) {
          elementList.forEach(element => {
            element.classList.remove("disabled")
          })
        }
      })
    }
  }

  const handleDeleteContact = () => {
    if (!contactID) return

    showNotificationPopup({
      message: 'Are you sure you want to delete this contact? This action cannot be undone.',
      cancelButton: { text: 'Cancel', color: 'var(--secondairy-light-medium-gray)' },
      actionButton: { text: 'Delete', color: 'var(--red)' },
      onAction: async () => {
        try {
          await toast.promise(
            httpClient.delete(import.meta.env.VITE_API_URL + `/api/contact/delete?contactID=${contactID}`),
            {
              pending: 'Deleting contact...',
              success: 'Contact deleted 👍',
              error: 'Delete failed 😢'
            }
          )
          navigate('/contacts')
        } catch (error) {
          const apiMsg = error?.response?.data?.message
          toast.error(apiMsg || 'Cannot delete contact. It may have related records.')
        }
      }
    })
  }

  const setStateTag = async (tagID, type = "add") => {
    if (type == "add") {
      const response = await httpClient.put(import.meta.env.VITE_API_URL + `/api/tags/add/${contactID}?tagID=${tagID}&type=contact`)
    }

    if (type == "remove") {
      const response = await httpClient.delete(import.meta.env.VITE_API_URL + `/api/tags/remove/${contactID}?tagID=${tagID}&type=contact`)
    }
  }

  const handleCreateNote = () => {
    setEditingNote(null)
    setNoteForm({ noteDate: dayjs(), noteType: 'Note', subject: '', content: '' })
    setNotePopupIsActive(true)
  }

  const handleEditNote = (note) => {
    setEditingNote(note)
    setNoteForm({
      noteDate: parseDateValue(note?.date_note) || dayjs(),
      noteType: note?.type || 'Note',
      subject: note?.subject || '',
      content: note?.note || ''
    })
    setNotePopupIsActive(true)
  }

  const handleSaveNewNote = async () => {
    const { subject, content, noteDate, noteType } = noteForm

    if (editingNote?.id) {
      const data = { subject, content, noteDate, noteType }

      await toast.promise(
        httpClient.put(import.meta.env.VITE_API_URL + `/api/notes/update/${editingNote.id}`, data),
        {
          pending: "Updating note...",
          success: "Note has been updated 👍",
          error: "Something went wrong 😢"
        }
      )

      fetchNotes()
      setEditingNote(null)
      setNoteForm({ noteDate: dayjs(), noteType: 'Note', subject: '', content: '' })
      setNotePopupIsActive(false)
      return
    }

    const data = {
      subject, content, noteDate, noteType, contactID, noteProject, noteBuilding, noteProperty, noteUnit
    }

    const response = await toast.promise(
      httpClient.post(import.meta.env.VITE_API_URL + `/api/notes/add`, data),
      {
        pending: "Creating note...",
        success: "Note has been created 👍",
        error: "Something went wrong 😢"
      }
    )
    console.log(response.data)

    fetchNotes()
    setNoteForm({ noteDate: dayjs(), noteType: 'Note', subject: '', content: '' })
    setNotePopupIsActive(false)
  }

  const handleDeleteNote = (note) => {
    if (!note?.id) {
      return
    }

    showNotificationPopup({
      message: 'Are you sure you want to delete this note? This action cannot be undone.',
      cancelButton: { text: 'Cancel', color: 'var(--secondairy-light-medium-gray)' },
      actionButton: { text: 'Delete', color: 'var(--red)' },
      onAction: async () => {
        try {
          await toast.promise(
            httpClient.delete(import.meta.env.VITE_API_URL + `/api/notes/delete/${note.id}`),
            {
              pending: 'Deleting note...',
              success: 'Note deleted 👍',
              error: 'Delete failed 😢'
            }
          )

          if (selectedNote?.id === note.id) {
            setSelectedNote(null)
            setNoteDetailPopupIsActive(false)
          }

          fetchNotes()
        } catch (error) {
          const apiMsg = error?.response?.data?.message
          toast.error(apiMsg || 'Cannot delete note.')
        }
      }
    })
  }

  const handlePropertyClick = (property) => {
    const projectId = property?.project?.id
    const units = Array.isArray(property?.units) ? property.units : []

    if (!projectId) {
      toast.error("No project linked to this property.")
      return
    }

    if (units.length === 0) {
      toast.error("No unit linked to this property.")
      return
    }

    // Default behavior: open the first linked unit
    const u = units[0]
    const unitId = typeof u?.id === 'object' ? u?.id?.id : u?.id
    const buildingId = typeof u?.building_id === 'object' ? u?.building_id?.id : u?.building_id

    if (!unitId || !buildingId) {
      toast.error("Unit link is missing building information.")
      return
    }

    navigate(`/projects/${projectId}/buildings/${buildingId}/units/${unitId}`)
  }

  const handleUnitTagClick = (e, property, unit) => {
    e.stopPropagation()
    const projectId = property?.project?.id
    const unitId = typeof unit?.id === 'object' ? unit?.id?.id : unit?.id
    const buildingId = typeof unit?.building_id === 'object' ? unit?.building_id?.id : unit?.building_id

    if (!projectId || !unitId || !buildingId) {
      toast.error("Cannot navigate to unit: missing link data.")
      return
    }
    navigate(`/projects/${projectId}/buildings/${buildingId}/units/${unitId}`)
  }

  return (
    <div className='template'>

      <Header>
        <div className="back_button" onClick={handleGoBack}><KeyboardBackspaceIcon /> Go Back</div>

        <div className='header__options--right'>
          <Button variant='primary' size='sm' id="contacts__btn-edit" onClick={handleEditContact}>{edit ? "Save" : "Edit Details"}</Button>
          <Button variant='secondary' tone='red' size='sm' onClick={handleDeleteContact}>Delete</Button>
        </div>
      </Header>

      <MenuBar />

      <div className="template__body" id='body-contact-detail'>

        <PopOutWindow popupID={"popout-contact-create-note"} isActive={notePopupIsActive} setIsActive={setNotePopupIsActive}>
          <div className='popout_window__header'>
            <Button variant='primary' size='sm' onClick={handleSaveNewNote}>{editingNote ? 'Update' : 'Save'}</Button>
          </div>

          <div className='form__section' >
            <div className="input_field">
              <label className="input_field__label" htmlFor="note_date">Note date</label>
              <DateField className="date-picker" format="DD-MM-YYYY" value={noteForm.noteDate} name='date' onChange={(newValue) => setNoteForm(prev => ({ ...prev, noteDate: newValue }))} />
            </div>

            <div className="input_field">
              <label className="input_field__label" htmlFor="note_type">Note type</label>
              <Dropdown
                index={"note-type"}
                listArray={Object.keys(noteTypes)}
                state={noteForm.noteType}
                setState={(value) => setNoteForm(prev => ({ ...prev, noteType: value }))}
                placeholder={"Select note type"}
                width={"50%"}
                height="40px"
              />
            </div>

            <div className="input_field">
              <label className="input_field__label" htmlFor="note_type">Project</label>
              <DropdownSpecialObject
                index={"note-projects"}
                listArray={projects}
                valueToShow="project_placeholder_name"
                valueToShow2="project_commercial_name"
                state={noteProject}
                setState={setNoteProject}
                placeholder={"Select project"}
                width={"50%"}
                height="40px"
              />
            </div>

            <div className="input_field">
              <label className="input_field__label" htmlFor="note_type">Building</label>
              <DropdownSpecialObject
                index={"note-buildings"}
                listArray={buildings}
                valueToShow="building_name"
                autoSelect={true}
                state={noteBuilding}
                setState={setNoteBuilding}
                placeholder={"Select building"}
                width={"50%"}
                height="40px"
              />
            </div>

            <div className="input_field">
              <label className="input_field__label" htmlFor="note_type">Unit</label>
              <DropdownSpecialObject
                index={"note-unit"}
                listArray={units}
                valueToShow="unit_name"
                autoSelect={true}
                state={noteUnit}
                setState={setNoteUnit}
                placeholder={"Select unit"}
                width={"50%"}
                height="40px"
              />
            </div>

            <div className="input_field">
              <label className="input_field__label" htmlFor="note_subject">Subject</label>
              <input className="input_field__input_box--text" id="note_subject" type="text" name="note_subject" value={noteForm.subject} onChange={(e) => setNoteForm(prev => ({ ...prev, subject: e.currentTarget.value }))} />
            </div>

            <div className="input_field">
              <label className="input_field__label" htmlFor="note_content">Content</label>
              <textarea className="input_field__input_box--text-area" id="note_content" type="text" name="note_content" value={noteForm.content} onChange={(e) => setNoteForm(prev => ({ ...prev, content: e.currentTarget.value }))} />
            </div>

          </div>
        </PopOutWindow>

        <div className={`body__section ${expandedSection === "contact-details" ? "full-screen" : ""}`} id="contact-details">
          <h2 onClick={() => handleToggleSection("contact-details")}>DETAILS</h2>
          <div className="form__section">

            <Dropdown
              index={"contact-title"}
              listArray={["Heer", "Mevrouw", "Juffrouw", "Professor", "Dokter", "Meester"]}
              state={contactTitle}
              setState={setContactTitle}
              placeholder={"Title"}
              width={"50%"}
            />

            <div className="input_field--combo-5050">
              <div className="input_field">
                <label className="input_field__label" htmlFor="first_name">First name</label>
                <input className="input_field__input_box--text" id="customers__first_name" type="text" name="first_name" defaultValue={contact.first_name} />
              </div>
              <div className="input_field">
                <label className="input_field__label" htmlFor="last_name">Last name</label>
                <input className="input_field__input_box--text" id="customers__last_name" type="text" name="last_name" defaultValue={contact.last_name} />
              </div>
            </div>

            <CheckBox
              text="Is married?"
              state={isMarried}
              setState={setIsMarried}
              width={"50%"}
              padding={"0px 2.5px"}
              margin={"7.5px 0"}
            />

            {
              isMarried &&
              <>
                <Dropdown
                  index={"contact-partner"}
                  listArray={contactsList}
                  setState={setPartner}
                  state={`${partner.first_name} ${partner.last_name}`}
                  placeholder={"Connect to partner"}
                  width={"100%"}
                  searchBar={true}
                />
              </>
            }

            {renderEmailField({
              label: 'Business email',
              inputId: 'customers__business_email',
              name: 'email',
              value: contact.email,
              errorId: 'input_error_business_email'
            })}

            {renderEmailField({
              label: 'Home email',
              inputId: 'customers__home_email',
              name: 'home_email',
              value: contact.home_email,
              errorId: 'input_error_home_email'
            })}

            <div className="input_field">
              <label className="input_field__label" htmlFor="tel">Business phone</label>
              <input className="input_field__input_box--text" id="customers__business_phone" type="text" name="business_phone" defaultValue={contact.business_phone} />
            </div>

            <div className="input_field">
              <label className="input_field__label" htmlFor="tel">Home phone</label>
              <input className="input_field__input_box--text" id="customers__home_phone" type="text" name="home_phone" defaultValue={contact.home_phone} />
            </div>

            <div className="input_field">
              <label className="input_field__label" htmlFor="tel">Mobile phone</label>
              <input className="input_field__input_box--text" id="customers__mobile_phone" type="text" name="mobile_phone" defaultValue={contact.mobile_phone} />
            </div>

          </div>

          <div className="form__section" id="address_info" ref={formSectionAddressInfo}>
            <div className="input_field">
              <label className="input_field__label" htmlFor="address_street">Address</label>
              <input className="input_field__input_box--text" id="customers__address" type="text" name="address" defaultValue={contact.address} />
            </div>
            <div className="input_field--combo-3070">
              <div className="input_field">
                <label className="input_field__label" htmlFor="address_pc">Postal code</label>
                <input className="input_field__input_box--text" id="customers__address_pc" type="text" name="postal" defaultValue={contact.postal} />
              </div>
              <div className="input_field">
                <label className="input_field__label" htmlFor="address_place">City</label>
                <input className="input_field__input_box--text" id="customers__address_place" type="text" name="city" defaultValue={contact.city} />
              </div>
            </div>

            <div className="input_field">
              <label className="input_field__label" htmlFor="address_province">Province</label>
              <input className="input_field__input_box--text" id="customers__address_place" type="text" name="province" defaultValue={contact.province} />
            </div>

            <div className="input_field">
              <label className="input_field__label" htmlFor="address_nr">Country</label>
              <input className="input_field__input_box--text" id="customers__address_country" type="text" name="country" defaultValue={contact.country} />
            </div>

            <div className="input_field">
              <label className="input_field__label" htmlFor="birthday">Birthday</label>
              <DateField className="date-picker" format="DD-MM-YYYY" value={birthday} onChange={(newValue) => setBirthday(newValue)} />
            </div>

            <div className="input_field">
              <label className="input_field__label" htmlFor="language">Language</label>
              <Dropdown
                index={"contact-language"}
                listArray={["Dutch", "English", "French", "Spanish", "Other"]}
                setState={setLanguage}
                state={language}
                width={"100%"}
                margin={"0"}
                parentDivRef={formSectionAddressInfo}
              />
            </div>
          </div>

        </div>

        <div className={`body__section ${expandedSection === "contact-properties" ? "full-screen" : ""}`} id="contact-properties">
          <h2 onClick={() => handleToggleSection("contact-properties")}>PROPERTIES</h2>
          <div className="form__section">
            <div className="menu__titles properties-subnav">
              <h3
                className={`${propertiesTab === "properties" ? "selected" : ""}`}
                onClick={() => setPropertiesTab("properties")}
              >
                properties
              </h3>
              <h3
                className={`${propertiesTab === "rentals" ? "selected" : ""}`}
                onClick={() => setPropertiesTab("rentals")}
              >
                rental contracts
              </h3>
            </div>

            {propertiesTab === "properties" ? (
              contactProperties.length === 0 ? (
                <div className="no-data-message">
                  <p>This contact does not own any properties.</p>
                </div>
              ) : (
                contactProperties.map((property, index) => (
                  <div
                    key={index}
                    className="property-item"
                    onClick={() => handlePropertyClick(property)}
                  >
                    <div className="property-header">
                      <div className="property-item-title">Cadastral Nr: {formatPropertyIdentifier(property.cadastral_nr, property.partition_nr)}</div>
                      {property.project && (
                        <span style={{ fontSize: '0.9em', color: '#666' }}>
                          Project: {property.project.project_placeholder_name || property.project.project_commercial_name}
                        </span>
                      )}
                    </div>

                    {property.units && property.units.length > 0 && (
                      <div className="property-units">
                        <div className="property-item-title">Units:</div>
                        <div className="units-container">
                          {property.units.map((unit, unitIndex) => (
                            <span
                              key={unitIndex}
                              className="unit-tag"
                              onClick={(e) => handleUnitTagClick(e, property, unit)}
                              title="Open unit"
                            >
                              {unit.unit_name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="property-click-hint">
                      Click to view project details
                    </div>
                  </div>
                ))
              )
            ) : (
              loadingRentalContracts ? (
                <LoadingSpinner />
              ) : (
                rentalContracts.length === 0 ? (
                  <div className="no-data-message">
                    <p>No rental contracts linked to this contact.</p>
                  </div>
                ) : (
                  rentalContracts.map((c, idx) => {
                    const unitNames = Array.isArray(c.units) ? c.units.map(u => u.unit_name).filter(Boolean) : []
                    const participantTypes = Array.from(new Set(
                      (Array.isArray(c.participants) ? c.participants : [])
                        .filter(p => p?.contact?.id == contactID)
                        .map(p => p?.participant_type)
                        .filter(Boolean)
                    ))
                    return (
                      <div
                        key={c.id || idx}
                        className="property-item"
                        onClick={() => handleRentalContractClick(c.id)}
                        title="Open rental contract"
                      >
                        <div className="property-header">
                          <div className="property-item-title">Contract Nr: {c.id}</div>
                          <span style={{ fontSize: '0.9em', color: '#666' }}>
                            {c.rental_type ? `Type: ${c.rental_type}` : "Type: N/A"}{typeof c.active === 'boolean' ? ` • ${c.active ? 'Active' : 'Inactive'}` : ''}
                          </span>
                          <span style={{ fontSize: '0.9em', color: '#666' }}>
                            Role: {participantTypes.length > 0 ? participantTypes.join(', ') : 'N/A'}
                          </span>
                        </div>

                        <div className="property-units">
                          <div className="property-item-title">Units:</div>
                          <div className="units-container">
                            {unitNames.length === 0 ? (
                              <span style={{ fontSize: '0.85em', color: '#999' }}>No units linked</span>
                            ) : (
                              unitNames.map((name, i) => (
                                <span key={i} className="unit-tag">{name}</span>
                              ))
                            )}
                          </div>
                        </div>

                        <div className="property-click-hint">
                          {c.start_date ? `Start: ${formatDate(c.start_date)}` : "Start: N/A"}{c.end_date ? ` • End: ${formatDate(c.end_date)}` : ""}
                        </div>
                      </div>
                    )
                  })
                )
              )
            )}
          </div>
        </div>

        <div className={`body__section ${expandedSection === "contact-notes" ? "full-screen" : ""}`} id="contact-notes">
          <PopupWindow popupID={"popup-contact-notes"} isActive={noteDetailPopupIsActive} setIsActive={setNoteDetailPopupIsActive}>
            {
              selectedNote != null &&
              <div className='note__container--popup note__container--popup--detail'>
                <div className='date_note--popup'>
                  <div className='popup_window__subtitle'>Date:</div>
                  <p>{formatDate(selectedNote.date_note)}</p>
                </div>
                <div className='date_note--popup'>
                  <div className='popup_window__subtitle'>Created by:</div>
                  <p>{getNoteCreatorName(selectedNote)}</p>
                </div>
                <div className='date_note--popup'>
                  <div className='popup_window__subtitle'>Subject:</div>
                  <p>{selectedNote.subject}</p>
                </div>
                <div className='note_text--popup'>
                  <div className='popup_window__subtitle'>Note:</div>
                  <p>{selectedNote.note}</p>
                </div>
              </div>
            }
          </PopupWindow>

          <h2 onClick={() => handleToggleSection("contact-notes")}>NOTES</h2>
          <div className="form__section">
            <button className='more--btn' onClick={handleCreateNote}>+</button>
            {
              notes.map((note, index) => {
                return (
                  <Note
                    popupWindowID="popup-contact-notes"
                    key={index}
                    noteObj={note}
                    type={note.type}
                    note={note.note}
                    subject={note.subject}
                    date={note.date_note}
                    setSelectedNote={setSelectedNote}
                    setPopupActive={setNoteDetailPopupIsActive}
                    onDelete={handleDeleteNote}
                    onEdit={handleEditNote}
                  />
                )
              })
            }
          </div>
        </div>

        <div className={`body__section ${expandedSection === "contact-tags" ? "full-screen" : ""}`} id="contact-tags">
          <h2 onClick={() => handleToggleSection("contact-tags")}>TAGS</h2>
          <div className="form__section">
            <DropdownMultiselectTags
              state={tags}
              listArray={tagsList}
              setState={setStateTag}
            />
          </div>
        </div>
        <div className={`body__section ${expandedSection === "contact-companies" ? "full-screen" : ""}`} id="contact-companies">
          <h2 onClick={() => handleToggleSection("contact-companies")}>COMPANIES</h2>
          <div className="form__section">
            {
              linkNewCompany ?
                loadingCompanyLink ?
                  <div className='flex' style={{ gap: "15px", marginTop: "10px" }}>
                    <div className='skeleton-loader' style={{ height: "30px", width: "100%" }}></div>
                    <div className='skeleton-loader' style={{ height: "30px", width: "50%" }}></div>
                  </div> :
                  <AddCompanyToContact
                    companiesList={companiesList}
                    companyState={""}
                    companyFunctionState={""}
                    setLoading={setLoadingCompanyLink}
                    contactID={contactID}
                    refreshData={fetchLinkedCompanies}
                    setLinkNewCompany={setLinkNewCompany}
                    maxHeightUL={expandedSection == "contact-companies" ? "350px" : "150px"}
                  /> :
                <button className='more--btn' onClick={() => setLinkNewCompany(true)}>+</button>
            }
            {
              companies.map((company, index) => {
                return (
                  <AddCompanyToContact
                    key={index}
                    companiesList={companiesList}
                    companyState={company.company_id.company_name}
                    companyIDState={company.company_id.id}
                    companyFunctionState={company.function}
                    contactID={contactID}
                    refreshData={fetchLinkedCompanies}
                    disabled={true}
                  />
                )
              })
            }
          </div>
        </div>
      </div>

    </div>
  )
}

export default ContactDetailPage
